/* Technical Anatomy Hub (Connector Overlay Edition)
   PERFORMANCE + UX FINAL + AUTO-ROTATION
   - Auto-rotates features every 4 seconds
   - Pauses on hover
   - Stops permanently on user interaction
   - NO stroke-dash draw animation (removed to avoid 1-frame glitch)
   - Click affordance: "nudge" animation
   - Connector redraw is scheduled via rAF
*/

(function () {
  function initHub(root) {
    const grid = root.querySelector('[data-tech-hub-grid]');
    const svg = root.querySelector('[data-connector-layer]');
    const productEl = root.querySelector('[data-connector="product"]');
    // Get all buttons (desktop + mobile clones)
    const buttons = Array.from(root.querySelectorAll('[data-tech-hub-button]'));
    // Get unique content panels to determine true feature count
    const contents = Array.from(root.querySelectorAll('.feature-content'));

    if (!grid || !svg || !productEl) return;

    let activeIndex = 0;
    
    // --- AUTO ROTATION SETTINGS ---
    let rotationInterval = null;
    const ROTATION_SPEED = 4000; // 4 seconds per slide
    let isHovering = false;
    
    // --- Perf: schedule drawing so we don't redraw multiple times in one frame ---
    let drawRAF = null;
    function scheduleDraw() {
      if (drawRAF) cancelAnimationFrame(drawRAF);
      drawRAF = requestAnimationFrame(() => {
        drawRAF = null;
        draw();
      });
    }

    // Affordance state
    let hasInteracted = false;
    let hasEnteredView = false;

    // Content jump prevention
    let minHeightLocked = false;

    function getCSSNumber(varName, fallback) {
      const v = getComputedStyle(root).getPropertyValue(varName).trim();
      if (!v) return fallback;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    }

    function getAccent() {
      const v = getComputedStyle(root).getPropertyValue('--hub-accent-color').trim();
      return v || '#0056b3';
    }

    function getNodeCount() {
      const n = Math.round(getCSSNumber('--hub-node-count', 4));
      return Math.max(0, Math.min(10, n));
    }

    function getNodeJitter() {
      const j = getCSSNumber('--hub-node-jitter', 0);
      return Math.max(0, Math.min(30, j));
    }

    function getProductAnchorYGap() {
      const g = getCSSNumber('--hub-product-anchor-y-gap', 26);
      return Math.max(0, Math.min(160, g));
    }

    // --- ROTATION LOGIC ---
    function stopRotation() {
      if (rotationInterval) {
        clearInterval(rotationInterval);
        rotationInterval = null;
      }
    }

    function startRotation() {
      // Do not restart if user has manually clicked/tapped
      if (hasInteracted) return;
      // Do not restart if currently hovering
      if (isHovering) return;

      stopRotation(); // Clear any existing to be safe
      
      rotationInterval = setInterval(() => {
        // Double check interaction state inside the loop
        if (hasInteracted || isHovering) {
          stopRotation();
          return;
        }
        
        // Calculate next index based on unique content length
        const featureCount = contents.length;
        if (featureCount === 0) return;
        
        const nextIndex = (activeIndex + 1) % featureCount;
        setActive(nextIndex);
      }, ROTATION_SPEED);
    }

    function markInteracted() {
      hasInteracted = true;
      stopRotation(); // Permanently stop auto-rotation
      root.classList.remove('is-hinting');
    }

    // Add/remove a class to trigger CSS animation without transform conflicts
    function nudgeButton(btn) {
      if (!btn) return;
      btn.classList.remove('tech-hub-nudge');
      // force reflow so animation restarts reliably
      void btn.offsetWidth;
      btn.classList.add('tech-hub-nudge');

      const onEnd = () => {
        btn.classList.remove('tech-hub-nudge');
        btn.removeEventListener('animationend', onEnd);
      };
      btn.addEventListener('animationend', onEnd);
    }

    function nudgeButtonsOnceStaggered() {
      if (hasInteracted) return;

      const desktopButtons = buttons.filter((b) => b.classList.contains('tech-icon-btn'));
      const list = desktopButtons.length ? desktopButtons : buttons;

      list.forEach((btn, i) => {
        setTimeout(() => {
          if (!hasInteracted) nudgeButton(btn);
        }, 140 * i);
      });
    }

    function getCenter(el) {
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2, rect: r };
    }

    function toLocal(pt) {
      const g = grid.getBoundingClientRect();
      return { x: pt.x - g.left, y: pt.y - g.top };
    }

    function getVisibleIcons() {
      return Array.from(root.querySelectorAll('[data-connector="icon"]')).filter((el) => {
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return false;

        const cs = getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;

        return true;
      });
    }

    function dist(a, b) {
      return Math.hypot(b.x - a.x, b.y - a.y);
    }

    function angleDeg(a, b) {
      return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    }

    function snapDeg(d, step) {
      return Math.round(d / step) * step;
    }

    function trimmedSegment(a, b, trimStartPx, trimEndPx) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 0.001) return null;

      const ux = dx / len;
      const uy = dy / len;

      const startT = Math.max(0, trimStartPx || 0);
      const endT = Math.min(len, len - (trimEndPx || 0));

      if (endT <= startT + 0.5) return null;

      return {
        a: { x: a.x + ux * startT, y: a.y + uy * startT },
        b: { x: a.x + ux * endT, y: a.y + uy * endT }
      };
    }

    // --- Hex node helpers (outlined) ---
    function hexPoints(cx, cy, r, rotationDeg = 0) {
      const rot = (Math.PI / 180) * rotationDeg;
      const pts = [];
      for (let k = 0; k < 6; k++) {
        const a = rot + k * (Math.PI / 3);
        pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
      }
      return pts.map((p) => p.join(',')).join(' ');
    }

    function createHexNode(cx, cy, r, strokeColor, opacity, rotationDeg = 0) {
      const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      poly.setAttribute('points', hexPoints(cx, cy, r, rotationDeg));
      poly.setAttribute('fill', 'none');
      poly.setAttribute('stroke', strokeColor);
      poly.setAttribute('stroke-width', '2');
      poly.setAttribute('vector-effect', 'non-scaling-stroke');
      poly.style.opacity = String(opacity);
      poly.style.pointerEvents = 'none';
      return poly;
    }

    // 60° bond path with forced first and last horizontal segments
    function buildBondPath(start, end, nodeCount, jitterPx, directionSign) {
      const pts = [{ x: start.x, y: start.y }];
      let cur = { x: start.x, y: start.y };

      const totalDist = dist(start, end);
      if (totalDist < 1) return { pts: [start, end] };

      const a0 = 0;
      const a1 = Math.PI / 3; // +60
      const a2 = -Math.PI / 3; // -60

      function stepAngleRad(kind) {
        if (kind === 'h') return directionSign > 0 ? a0 : Math.PI;
        if (kind === 'up') return directionSign > 0 ? a1 : Math.PI - a1;
        return directionSign > 0 ? a2 : Math.PI - a2;
      }

      const minHoriz = Math.min(90, Math.max(40, totalDist * 0.18));
      const preEnd = { x: end.x - directionSign * minHoriz, y: end.y };

      // forced first horizontal
      const firstStepLen = Math.min(minHoriz, dist(cur, preEnd) * 0.35);
      cur = { x: cur.x + directionSign * firstStepLen, y: cur.y };
      pts.push(cur);

      const remainingNodes = Math.max(0, nodeCount - 1);

      for (let i = 1; i <= remainingNodes; i++) {
        const remainingSegments = remainingNodes - i + 1 + 1; // interior remaining + final hop to preEnd
        const remainingDist = dist(cur, preEnd);
        const stepLen = remainingDist / remainingSegments;

        const useDiagonal = i % 2 === 1;
        const diagonalUp = i % 4 < 2;

        const theta = useDiagonal
          ? stepAngleRad(diagonalUp ? 'up' : 'down')
          : stepAngleRad('h');

        let nx = cur.x + Math.cos(theta) * stepLen;
        let ny = cur.y + Math.sin(theta) * stepLen;

        if (jitterPx > 0) {
          const perp = theta + Math.PI / 2;
          const s = i % 2 === 0 ? 1 : -1;
          nx += Math.cos(perp) * jitterPx * s;
          ny += Math.sin(perp) * jitterPx * s;
        }

        cur = { x: nx, y: ny };
        pts.push(cur);
      }

      // preEnd + forced last horizontal
      pts.push({ x: preEnd.x, y: preEnd.y });
      pts.push({ x: end.x, y: end.y });

      return { pts };
    }

    // Use kickDraw only for initial load / image load. Not on clicks.
    function kickDraw() {
      scheduleDraw();
      requestAnimationFrame(scheduleDraw);
      setTimeout(scheduleDraw, 60);
      setTimeout(scheduleDraw, 260);
    }

    function draw() {
      if (window.matchMedia('(max-width: 767px)').matches) return;

      const g = grid.getBoundingClientRect();
      if (g.width === 0 || g.height === 0) return;

      const visibleIcons = getVisibleIcons();
      if (visibleIcons.length === 0) return;

      svg.setAttribute('viewBox', `0 0 ${g.width} ${g.height}`);
      svg.setAttribute('width', g.width);
      svg.setAttribute('height', g.height);
      svg.innerHTML = '';

      const prod = getCenter(productEl);
      const prodCenter = toLocal({ x: prod.x, y: prod.y });
      const prodRect = prod.rect;

      const leftAnchorX = prodCenter.x - prodRect.width * 0.28;
      const rightAnchorX = prodCenter.x + prodRect.width * 0.28;

      const iconEdgeGap = 6;
      const inactiveStroke = 2;
      const activeStroke = 3;

      const nodeGap = 3;

      // Taper toward product
      const taperAmount = 0.18;
      const taperStart = 0.25;

      const nodeCount = getNodeCount();
      const jitter = getNodeJitter();
      const anchorGap = getProductAnchorYGap();

      visibleIcons.forEach((icon) => {
        const idx = parseInt(icon.dataset.index, 10);
        if (Number.isNaN(idx)) return;

        const iconAbs = getCenter(icon);
        const iconCenter = toLocal({ x: iconAbs.x, y: iconAbs.y });
        const iconRect = iconAbs.rect;

        const isLeftSide = iconCenter.x < prodCenter.x;
        const directionSign = isLeftSide ? 1 : -1;

        // Start = midpoint of edge nearest the product
        const start = {
          x: isLeftSide
            ? iconCenter.x + iconRect.width / 2 + iconEdgeGap
            : iconCenter.x - iconRect.width / 2 - iconEdgeGap,
          y: iconCenter.y
        };

        // End = tin anchor with symmetric vertical spread (controlled by --hub-product-anchor-y-gap)
        const dir = iconCenter.y < prodCenter.y ? -1 : 1;
        const sep = dir * (anchorGap / 2);

        const end = {
          x: isLeftSide ? leftAnchorX : rightAnchorX,
          y: prodCenter.y + sep
        };

        const isActive = idx === activeIndex;
        const inactive = '#e0e0e0';
        const active = getAccent();

        const strokeBase = isActive ? activeStroke : inactiveStroke;
        const strokeColor = isActive ? active : inactive;
        const opacityBase = isActive ? 1 : 0.85;

        const molecule = buildBondPath(start, end, nodeCount, jitter, directionSign);

        const nodeRadiusByIndex = {};
        for (let i = 1; i < molecule.pts.length - 1; i++) {
          nodeRadiusByIndex[i] = i % 2 === 0 ? 7 : 6;
        }

        const segCount = molecule.pts.length - 1;

        // segments (trimmed + tapered)
        for (let i = 0; i < segCount; i++) {
          const p1 = molecule.pts[i];
          const p2 = molecule.pts[i + 1];

          const trimStart = nodeRadiusByIndex[i] ? nodeRadiusByIndex[i] + nodeGap : 0;
          const trimEnd = nodeRadiusByIndex[i + 1] ? nodeRadiusByIndex[i + 1] + nodeGap : 0;

          const seg = trimmedSegment(p1, p2, trimStart, trimEnd);
          if (!seg) continue;

          const t = segCount <= 1 ? 1 : i / (segCount - 1);
          const taperT = Math.max(0, (t - taperStart) / (1 - taperStart));
          const width = strokeBase * (1 - taperAmount * taperT);

          const segPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          segPath.setAttribute('d', `M ${seg.a.x} ${seg.a.y} L ${seg.b.x} ${seg.b.y}`);
          segPath.setAttribute('fill', 'none');
          segPath.setAttribute('stroke-linecap', 'round');
          segPath.setAttribute('stroke-linejoin', 'round');
          segPath.setAttribute('vector-effect', 'non-scaling-stroke');
          segPath.setAttribute('stroke', strokeColor);
          segPath.setAttribute('stroke-width', String(width));
          segPath.style.opacity = String(opacityBase);
          segPath.style.pointerEvents = 'none';

          // IMPORTANT: no SVG filters here (drop-shadow is expensive and causes low FPS)
          svg.appendChild(segPath);
        }

        // hex nodes (rotation aligned to local bond direction)
        const rotationSnap = 30;

        molecule.pts.forEach((p, i) => {
          const isEndpoint = i === 0 || i === molecule.pts.length - 1;
          if (isEndpoint) return;

          const r = nodeRadiusByIndex[i] || 6;
          const prev = molecule.pts[i - 1] || molecule.pts[i];
          const next = molecule.pts[i + 1] || molecule.pts[i];

          const aIn = angleDeg(prev, p);
          const aOut = angleDeg(p, next);

          const toRad = (d) => (d * Math.PI) / 180;
          const toDeg = (r2) => (r2 * 180) / Math.PI;

          const vx = Math.cos(toRad(aIn)) + Math.cos(toRad(aOut));
          const vy = Math.sin(toRad(aIn)) + Math.sin(toRad(aOut));
          const aAvg = toDeg(Math.atan2(vy, vx));

          const rot = snapDeg(aAvg, rotationSnap) + 30;

          const op = isActive ? 1 : 0.55;
          svg.appendChild(createHexNode(p.x, p.y, r, strokeColor, op, rot));
        });
      });
    }

    // Lock content wrap height to avoid jump, and do fade swap without affecting layout mid-transition
    function setActive(index) {
      activeIndex = index;

      // buttons
      buttons.forEach((b) => b.classList.remove('active'));
      root.querySelectorAll(`[data-index="${index}"]`).forEach((b) => b.classList.add('active'));

      const wrap = root.querySelector('[data-tech-hub-content]') || root.querySelector('.tech-info-display');
      if (!wrap) {
        scheduleDraw();
        return;
      }

      const incoming = root.querySelector(`#feature-content-${index}`);
      if (!incoming) {
        scheduleDraw();
        return;
      }

      const outgoing = wrap.querySelector('.feature-content.active');

      if (outgoing === incoming) {
        scheduleDraw();
        return;
      }

      // On first interaction, set a stable minHeight based on the tallest panel (prevents any "jump down then back")
      if (!minHeightLocked) {
        minHeightLocked = true;

        // Temporarily measure all panels (without permanently showing them)
        const prevMin = wrap.style.minHeight;
        let maxH = 0;

        contents.forEach((panel) => {
          const prevDisplay = panel.style.display;
          const prevPos = panel.style.position;
          const prevOpacity = panel.style.opacity;

          panel.style.display = 'block';
          panel.style.position = 'relative';
          panel.style.opacity = '1';

          const h = panel.getBoundingClientRect().height;
          if (h > maxH) maxH = h;

          panel.style.display = prevDisplay;
          panel.style.position = prevPos;
          panel.style.opacity = prevOpacity;
        });

        if (maxH > 0) wrap.style.minHeight = `${Math.ceil(maxH)}px`;
        else wrap.style.minHeight = prevMin || '';
      }

      // OUTGOING: take it out of flow so it can't change container height while fading
      if (outgoing) {
        outgoing.classList.remove('active');
        outgoing.classList.add('is-leaving');
        outgoing.style.position = 'absolute';
        outgoing.style.left = '0';
        outgoing.style.right = '0';
        outgoing.style.top = '0';
        outgoing.style.width = '100%';
        outgoing.style.pointerEvents = 'none';
        outgoing.style.opacity = '0';
      }

      // INCOMING: ensure it's in flow and visible, then fade in
      incoming.style.display = 'block';
      incoming.style.position = 'relative';
      incoming.style.left = '';
      incoming.style.right = '';
      incoming.style.top = '';
      incoming.style.width = '';
      incoming.style.pointerEvents = '';
      incoming.style.opacity = '0';

      requestAnimationFrame(() => {
        incoming.classList.add('active');
        incoming.style.opacity = '1';

        // Cleanup outgoing after fade duration
        setTimeout(() => {
          if (outgoing) {
            outgoing.style.display = 'none';
            outgoing.classList.remove('is-leaving');
            outgoing.style.position = '';
            outgoing.style.left = '';
            outgoing.style.right = '';
            outgoing.style.top = '';
            outgoing.style.width = '';
            outgoing.style.pointerEvents = '';
            outgoing.style.opacity = '';
          }
        }, 300); // match your CSS transition duration
      });

      scheduleDraw();
    }

    function onClick(e) {
      const btn = e.target.closest('[data-tech-hub-button]');
      if (!btn || !root.contains(btn)) return;

      const idx = parseInt(btn.dataset.index, 10);
      if (Number.isNaN(idx)) return;

      markInteracted(); // Stops auto-rotation permanently
      setActive(idx);

      // “Clickable” affordance (no transforms)
      nudgeButton(btn);
    }
    
    function onMouseEnter() {
      isHovering = true;
      stopRotation();
    }
    
    function onMouseLeave() {
      isHovering = false;
      startRotation();
    }

    function onResize() {
      scheduleDraw();
    }

    root.addEventListener('click', onClick);
    root.addEventListener('pointerdown', markInteracted, { passive: true });
    
    // Pause on hover handlers
    root.addEventListener('mouseenter', onMouseEnter);
    root.addEventListener('mouseleave', onMouseLeave);
    
    window.addEventListener('resize', onResize, { passive: true });

    // redraw on image load + window load
    const imgs = Array.from(root.querySelectorAll('img'));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener('load', kickDraw, { once: true });
    });
    window.addEventListener('load', kickDraw, { once: true });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => scheduleDraw());
      ro.observe(grid);
    }

    // In-view: nudge once (staggered), but ONLY before interaction
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            root.classList.add('is-inview');

            if (!hasEnteredView) {
              hasEnteredView = true;

              // allow layout to settle
              requestAnimationFrame(() => {
                scheduleDraw();
                // Start the show
                startRotation();
                setTimeout(() => nudgeButtonsOnceStaggered(), 350);
              });
            }
          });
        },
        { threshold: 0.35 }
      );

      io.observe(root);
    } else {
      root.classList.add('is-inview');
      setTimeout(() => nudgeButtonsOnceStaggered(), 500);
      startRotation();
    }

    // Initial render
    requestAnimationFrame(() => {
      setActive(0);
      kickDraw();
    });
  }

  function boot() {
    document.querySelectorAll('[data-tech-hub]').forEach(initHub);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();